import { Component, Input } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, DialogEvents, ListMode, PaymentContext, PaymentSourceMode, PaymentSourcesScope, SimpleDialogData } from '@sb/library/base';
import { ErrorCode, PaymentMethod, PaymentSourceDisplayItem, PaymentSourceSearchOptions, PaymentSourceType, PaymentSourceUpdateItem } from '@sb/library/data-models';
import { DialogDeleteComponent, DialogInfoComponent } from '@sb/library/dialog';
import { Reordering } from '@sb/library/drag-and-drop';
import { SBDate } from '@sb/library/localization';

@Component({
    selector: 'lib-payment-sources',
    templateUrl: './payment-sources.component.html',
    styleUrls: ['./payment-sources.component.scss']
})
export class PaymentSourcesComponent extends BaseViewComponent {

    Scope = this.SetScope({
        Requires: PaymentSourcesScope
    });

    @Input() parentID!: string;

    private _loading: boolean = true;
    private _isEmptyState: boolean = false;
    private _numPaymentMethods: number = 0;
    private _paymentSources: PaymentSourceDisplayItem[] = [];
    private _selectedPaymentSource: PaymentSourceDisplayItem | undefined;

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.Scope.paymentSources) {
            this._paymentSources = this.Scope.paymentSources;
            if (this._paymentSources.length > 0) {
                this.PaymentSourceSelected(this._paymentSources[0]);
            }
        } else {
            this.FetchData();
        }

        this.ConnectTrigger(this.Scope.triggerRefetchForParentID, this.RefetchForParentID);
    }

    Reordered(event: Reordering<PaymentSourceDisplayItem>) {
        ApiRoutes.PaymentSource.Update.Call({
            Parameter: event.Item.ID!,
            Body: new PaymentSourceUpdateItem({
                AutoPay: event.Item.AutoPay,
                OrderIndex: event.ToOrderIndex
            })
        }).subscribe(_ => this.FetchData());
    }

    MakeDefault(item: PaymentSourceDisplayItem) {
        ApiRoutes.PaymentSource.Update.Call({
            Parameter: item.ID!,
            Body: new PaymentSourceUpdateItem({
                AutoPay: item.AutoPay,
                OrderIndex: 0
            })
        }).subscribe(_ => {
            this.FetchData();
        });

        // Fake the reordering until the API returns
        item.OrderIndex = -1;
        this._paymentSources = JSON.parse(JSON.stringify(this._paymentSources));  // there must be a better way to do this
    }

    DeleteItem(item: PaymentSourceDisplayItem) {
        const dialog = this.CreateDialog(DialogDeleteComponent, {
            Title: $localize`:@@CommonPaymentSourceConfirmRemoveCreditCardTitle:Remove Payment Method`,
            Text: $localize`:@@CommonPaymentSourceConfirmRemovePaymentMethodText:Are you sure you want to remove the selected payment method? This action cannot be undone.`
        });

        dialog.events.subscribe(event => {
            if (event === DialogEvents.PrimaryAction) {
                ApiRoutes.PaymentSource.Delete.Call({ Parameter: item.ID! }).subscribe({
                    next: _ => {
                        dialog.DismissAction();
                        this.FetchData();
                    },
                    error: (error: typeof ApiRoutes.PaymentSource.Delete.Error) => {
                        if (error.Response.ErrorCode === ErrorCode.PaymentSourceRequired) {
                            this.ShowPaymentSourceRequiredDialog();
                        } else {
                            throw error;
                        }
                    }
                });
            }
        });
    }

    ToggleAutoPay(item: PaymentSourceDisplayItem, autoPayEnabled: boolean) {
        ApiRoutes.PaymentSource.Update.Call({
            Parameter: item.ID!,
            Body: new PaymentSourceUpdateItem({
                AutoPay: autoPayEnabled,
                OrderIndex: item.OrderIndex
            })
        }).subscribe(_ => {
            this.FetchData();
        });

        item.AutoPay = autoPayEnabled;
    }

    PaymentSourceSelected(paymentSource: PaymentSourceDisplayItem): void {
        this._selectedPaymentSource = paymentSource;
        this.Scope.PaymentSourceSelected?.(paymentSource);
    }

    CreateExtendedItem(paymentSource: PaymentSourceDisplayItem, index: number): PaymentSourceDisplayItem {
        paymentSource.index = index;
        return paymentSource;
    }

    Verify(paymentSource: PaymentSourceDisplayItem): void {
        if (paymentSource.VerificationUrl) {
            window.open(paymentSource.VerificationUrl, '_blank');
            this.PollForVerification(paymentSource);
        }
    }

    private PollForVerification(paymentSource: PaymentSourceDisplayItem) {
        setTimeout(() => {
            ApiRoutes.PaymentSource.Get.Call({
                Parameter: paymentSource.ID!
            }).subscribe({
                next: result => {
                    if (result.IsVerified) {
                        this.FetchData();
                    } else {
                        this.PollForVerification(paymentSource);
                    }
                },
                error: _ => {
                    // In the case of an error (say, if the card has been deleted), just silently stop polling
                }
            })
        }, 2000);
    }

    FormatExpiry(date: string): string {
        const expiry = SBDate.FromISO(date);
        return `${expiry.month.toString().padStart(2, '0')}/${expiry.year.toString().slice(-2)}`;
    }

    FindFirstVerifiedSource(paymentSource: PaymentSourceDisplayItem): boolean {
        const verifiedPaymentSourceIndex = this._paymentSources.findIndex(p => paymentSource.ID == p.ID);
        return (paymentSource.IsVerified && verifiedPaymentSourceIndex === 0);
    }

    private RefetchForParentID(parentID: string) {
        if (parentID === this.parentID) {
            this.FetchData();
        }
    }

    private FetchData(): void {
        const paymentSourcesSearchOptions = new PaymentSourceSearchOptions();
        paymentSourcesSearchOptions.ParentIDs = this.parentID ? [this.parentID] : [];

        ApiRoutes.PaymentSource.Search.Call({
            Body: paymentSourcesSearchOptions
        }).subscribe(data => {
            this._loading = false;
            this._paymentSources = data.ItemSubset;
            this._isEmptyState = data.ItemSubset.length === 0;
            this._numPaymentMethods = data.TotalItemCount;
            if (this._paymentSources.length > 0) {
                this.PaymentSourceSelected(this._paymentSources[0]);
            }
        });
    }

    private ShowPaymentSourceRequiredDialog() {
        const dialog = this.CreateDialog(DialogInfoComponent, new SimpleDialogData({
            Title: $localize`:@@CommonPaymentSourceAtLeastOnePaymentSourceRequiredErrorTitle:Payment Source Required`,
            Text: $localize`:@@CommonPaymentSourceAtLeastOnePaymentSourceRequiredErrorText:Business policy requires that all families have at least one verified payment source on file. Please add another credit card (or bank account, if available) before removing this one.`
        }));
        dialog.events.subscribe(event => {
            if (event === DialogEvents.PrimaryAction) {
                dialog.DismissAction();
            }
        });
    }

    get paymentSources(): PaymentSourceDisplayItem[] {
        return this._paymentSources;
    }

    get loading(): boolean {
        return this._loading;
    }

    get PaymentSourceDisplayItem(): typeof PaymentSourceDisplayItem {
        return PaymentSourceDisplayItem;
    }

    get PaymentMethod(): typeof PaymentMethod {
        return PaymentMethod;
    }

    get PaymentSourceType(): typeof PaymentSourceType {
        return PaymentSourceType;
    }

    get isEmptyState(): boolean {
        return this._isEmptyState;
    }

    get hasContent(): boolean {
        return !this._isEmptyState;
    }

    get numPaymentMethods(): number {
        return this._numPaymentMethods;
    }

    get selectedPaymentSource(): PaymentSourceDisplayItem | undefined {
        return this._selectedPaymentSource;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }

    get ListMode(): typeof ListMode {
        return ListMode;
    }
}
